import React from 'react';
import classes from './AdvancedFeature.module.scss';
import { Icon } from 'components/images/Icon/Icon';
import { IconsEnum } from 'constants/icons';
import { Tooltip } from 'components/actions/Tooltip';
import { useOrganizationPlan } from 'hooks/useOrganizationPlan';
import { Button } from 'components/actions/Button';
import { ButtonColorsEnum } from 'constants/button-colors';
import scssVars from '../../../assets/scss/exportedVars.module.scss';
import { Link } from 'react-router-dom';
import { useBreakpoints } from 'hooks/useBreakpoints';

export enum AdvancedFeatures {
  'google',
  'filterFeed',
  'CSV',
  'OPML',
  'analyticsFilter'
}

type AdvancedFeaturesProps = {
  feature: AdvancedFeatures;
};
export const AdvancedFeatureView = ({ feature }: AdvancedFeaturesProps) => {
  let tooltipContent: JSX.Element = <></>;
  const { isOwner, isTrial } = useOrganizationPlan();
  const breakpoint = useBreakpoints();

  if (feature === AdvancedFeatures.google) {
    let tooltipText: JSX.Element;

    if (isTrial) {
      tooltipText = isOwner ? (
        <>
          If you would like to keep using Google Analytics UTM features after your free trial, please ensure you select
          a plan with <strong>Intermediate Analytics.</strong>
        </>
      ) : (
        <>
          If you would like to keep using Google Analytics UTM features after the free trial, please ensure the
          Organization Owner selects a plan with <strong>Intermediate Analytics.</strong>
        </>
      );
    } else {
      tooltipText = isOwner ? (
        <>
          If you want to add Google Analytics UTM tracking parameters, you will need to upgrade to a plan with
          Intermediate Analytics
        </>
      ) : (
        <>Contact the Organization Owner to upgrade to a plan with Intermediate Analytics to access this feature</>
      );
    }
    tooltipContent = (
      <>
        {tooltipText}
        {isOwner && (
          <div className={classes.TooltipButtonContainer}>
            <Button color={ButtonColorsEnum.secondary} size="medium" variant="solid" type="button">
              <Link
                className={classes.Link}
                to={'/settings/manage-subscription?src=plan_limit_reached&limit=analytics&context=google_utm'}
                target="_blank">
                View plans
              </Link>
            </Button>
          </div>
        )}
      </>
    );
  } else if (feature === AdvancedFeatures.filterFeed) {
    let tooltipText: JSX.Element;

    if (isTrial) {
      tooltipText = isOwner ? (
        <>
          If you would like to keep using these sorting and filtering options after your free trial, please ensure you
          select a plan with the <strong>Advanced Importer.</strong>
        </>
      ) : (
        <>
          If you would like to keep using these sorting and filtering options after the free trial, please ensure the
          Organization Owner selects a plan with the <strong>Advanced Importer.</strong>
        </>
      );
    } else {
      tooltipText = isOwner ? (
        <>Upgrade to a plan with Advanced Analytics to access this feature</>
      ) : (
        <>Contact the Organization Owner to upgrade to a plan with Advanced Analytics to access this feature</>
      );
    }
    tooltipContent = (
      <>
        {tooltipText}
        {isOwner && (
          <div className={classes.TooltipButtonContainer}>
            <Button color={ButtonColorsEnum.secondary} size="medium" variant="solid" type="button">
              <Link
                className={classes.Link}
                to={'/settings/manage-subscription?src=plan_limit_reached&limit=importer&context=filter'}
                target="_blank">
                View plans
              </Link>
            </Button>
          </div>
        )}
      </>
    );
  } else if (feature === AdvancedFeatures.CSV) {
    let content: JSX.Element;
    if (isTrial) {
      content = isOwner ? (
        <>
          If you would like to keep using the CSV Importer after your free trial, please ensure you select a plan with
          the <strong>Intermediate Importer.</strong>{' '}
        </>
      ) : (
        <>
          If you would like to keep using the CSV Importer after the free trial, please ensure the Organization Owner
          selects a plan with the <strong>Intermediate Importer.</strong>{' '}
        </>
      );
    } else {
      content = isOwner ? (
        <>Upgrade to Intermediate Importer. Save time by importing from CSV spreadsheets. </>
      ) : (
        <>
          Please contact the Organization Owner to upgrade to Intermediate Importer. Save time by importing from CSV
          spreadsheets.
        </>
      );
    }
    return (
      <div className={classes.ContainerCsv}>
        <span>{content}</span>
        <div className={classes.ButtonContainer}>
          {isOwner && (
            <Button color={ButtonColorsEnum.secondary} size="medium" variant="solid" type="button">
              <Link
                className={classes.Link}
                to={'/settings/manage-subscription?src=plan_limit_reached&limit=importer&context=csv'}
                target="_blank">
                View plans
              </Link>
            </Button>
          )}
        </div>
      </div>
    );
  } else if (feature === AdvancedFeatures.OPML) {
    let content: JSX.Element;
    if (isTrial) {
      content = isOwner ? (
        <>
          If you would like to import more Feedly bookmarks after your free trial, please ensure you select a plan with
          the <strong>Advanced Importer.</strong>{' '}
        </>
      ) : (
        <>
          If you would like to import more Feedly bookmarks after the free trial, please ensure the Organization Owner
          selects a plan with the <strong>Advanced Importer.</strong>
        </>
      );
    } else {
      content = isOwner ? (
        <>
          Upgrade to a plan with Advanced Importer, to Import Feedly Bookmarks and add posts from your favorite RSS
          Feeds
        </>
      ) : (
        <>
          Please contact the Organization Owner to upgrade to a plan with Advanced Importer, to Import Feedly Bookmarks
          and add posts from your favorite RSS Feeds{' '}
        </>
      );
    }
    return (
      <div className={classes.ContainerCsv}>
        <span>{content}</span>
        <div className={classes.ButtonContainer}>
          {isOwner && (
            <Button color={ButtonColorsEnum.secondary} size="medium" variant="solid" type="button">
              <Link
                className={classes.Link}
                to={'/settings/manage-subscription?src=plan_limit_reached&limit=importer&context=feedly'}
                target="_blank">
                View plans
              </Link>
            </Button>
          )}
        </div>
      </div>
    );
  } else if (feature === AdvancedFeatures.analyticsFilter) {
    let tooltipText: JSX.Element;
    if (isTrial) {
      tooltipText = isOwner ? (
        <>
          If you would like to view reports for any period after the free trial, please select a plan with Advanced
          Analytics.
        </>
      ) : (
        <>
          If you would like to view reports for any period after the free trial, please ensure the Organization Owner
          selects a plan with Advanced Analytics.
        </>
      );
    } else {
      tooltipText = isOwner ? (
        <>
          If you would like to view reports for any period, please upgrade to a plan with Advanced Analytics. Otherwise
          you can only view reports for the last month.
        </>
      ) : (
        <>
          Please contact Organization Owner to upgrade to a plan with Advanced Analytics to view reports for any period.
          Otherwise you can only view reports for the last month.
        </>
      );
    }
    tooltipContent = (
      <>
        {tooltipText}
        {isOwner && (
          <div className={classes.TooltipButtonContainer}>
            <Button color={ButtonColorsEnum.secondary} size="medium" variant="solid" type="button">
              <Link
                className={classes.Link}
                to={'/settings/manage-subscription?src=plan_limit_reached&limit=analytics&context=reports'}
                target="_blank">
                View plans
              </Link>
            </Button>
          </div>
        )}
      </>
    );
  }

  const hideText =
    feature === AdvancedFeatures.analyticsFilter && (breakpoint === 'large' || breakpoint === 'tabletportrait');
  return (
    <>
      <div id="UpgradeAccess" className={classes.Container}>
        <Icon name={IconsEnum.crown} size="small"></Icon>
        {!hideText && <span className={classes.Text}> {isTrial ? 'PREMIUM FEATURE' : 'Upgrade to access'}</span>}
      </div>
      <Tooltip
        target={'#UpgradeAccess'}
        type="regular"
        pointer
        autoHide={false}
        autoZIndex={false}
        style={{ zIndex: scssVars['z-index-overmodal'] }}>
        {tooltipContent}
      </Tooltip>
    </>
  );
};

export default AdvancedFeatureView;
